<template>
  <b-modal
    v-model="isOpen"
    centered
    modal-class="cy-current-item-modal"
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-container fluid>
      <b-button
        @click="close"
        class="cy-current-item-close d-flex position-absolute close-btn"
      >
        <img class="close-icon" src="@/assets/icons/close_sidebar.svg" alt />
      </b-button>

      <b-row class="mt-5">
        <b-col md="12" data-cy="company-add-edit-model">
          <ValidationObserver ref="observer">
            <b-form>
              <b-form-group class="d-flex primary-dark general-info-form-group">
                <b-card no-body class="d-flex flex-column general-info-card">
                  <div class="d-flex flex-wrap mt-2">
                    <b-row class="w-100 my-0 mx-auto mb-4 pb-2">
                      <b-col md="3">
                        <AvatarItem
                          :name="name"
                          :rem-size="8.4"
                          :avatarThumbnail="companyImageFile"
                          :disabled="isProcessing"
                        />
                      </b-col>

                      <b-col md="9">
                        <label
                          class="
                            font-sm
                            file-select
                            mt-3
                            pt-4
                            cursor-pointer
                            action-text
                          "
                        >
                          <p class="secondary--text">Click to upload/update</p>
                          <p class="secondary--text reduce-whitespace">
                            Company logo *
                          </p>
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            :disabled="isProcessing"
                            @click="uploadClear"
                            @change="uploadFile"
                          />
                        </label>
                        <h6
                          v-if="photoError && !isProcessing"
                          class="
                            font-sm font-family-regular
                            position-relative
                            danger
                          "
                        >
                          Please update profile picture
                        </h6>
                        <h6
                          v-if="photoFileTypeError && !isProcessing"
                          class="
                            font-sm font-family-regular
                            position-relative
                            danger
                          "
                        >
                          Incorrect file type. Please upload an image.
                        </h6>
                        <h6
                          v-if="photoTokenError && !isProcessing"
                          class="
                            font-sm font-family-regular
                            position-relative
                            danger
                          "
                        >
                          User token expired. Please logout and login again.
                        </h6>
                      </b-col>
                    </b-row>

                    <b-row class="w-100 my-0 mx-auto mt-1">
                      <b-col class="mb-4" md="12">
                        <ValidationProvider
                          name="Company name"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            class="position-relative"
                            label="Company name *"
                          >
                            <b-form-input
                              v-model="updatedCompany.name"
                              :disabled="isProcessing"
                              type="text"
                              placeholder="Enter company name"
                              data-cy="company-edit-field-name"
                            ></b-form-input>
                            <h6
                              v-if="errors[0] && !isProcessing"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              {{ errors[0] }}
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row class="w-100 my-0 mx-auto">
                      <b-col class="mb-4 mt-3" md="12">
                        <ValidationProvider
                          name="Public Support Billing Entity"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <b-form-group label="Public Support Billing Entity *">
                            <b-form-select
                              v-model="updatedCompany.headroomEntity"
                              :options="headroomEntityOptions"
                              :disabled="isProcessing"
                              @change="updateHeadroomEntity"
                              data-cy="company-edit-field-headroom-entity"
                            ></b-form-select>
                            <h6
                              v-if="errors[0] && !isProcessing"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              {{ errors[0] }}
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row v-if="isNewCompany" class="w-100 my-0 mx-auto">
                      <b-col class="mb-4 mt-3" md="12">
                        <ValidationProvider
                          name="Subscription Plan"
                          v-slot="{ errors }"
                        >
                          <b-form-group label="Subscription Plan">
                            <b-form-select
                              v-model="updatedCompany.new_membership"
                              :options="memberships"
                              :disabled="isProcessing"
                              @change="updateMembership"
                              data-cy="company-edit-field-membership"
                            ></b-form-select>
                            <h6
                              v-if="errors[0] && !isProcessing"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              {{ errors[0] }}
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row
                      v-if="isNewCompany && allowsFlexibleHours"
                      class="w-100 my-0 mx-auto"
                    >
                      <b-col class="mb-4 mt-3" md="12">
                        <ValidationProvider
                          name="'Subscription Hours'"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            class="position-relative"
                            label="Subscription hours per month*"
                          >
                            <b-form-input
                              v-model="updatedCompany.total_hours"
                              autocomplete="off"
                              placeholder="Enter subscription hours"
                              type="number"
                              min="0"
                              data-cy="company-edit-field-subscription-hours"
                            ></b-form-input>
                          </b-form-group>

                          <h6
                            v-if="errors[0] && !isProcessing"
                            class="
                              font-sm font-family-regular
                              position-absolute
                              danger
                            "
                          >
                            {{ errors[0] }}
                          </h6>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row class="w-100 my-0 mx-auto">
                      <b-col class="mb-4 mt-3" md="12">
                        <ValidationProvider name="Country" v-slot="{ errors }">
                          <b-form-group label="Country">
                            <b-form-select
                              v-model="updatedCompany.country"
                              :options="countries"
                              :disabled="isProcessing"
                              @change="updateCountry"
                              data-cy="company-edit-field-country"
                            ></b-form-select>
                            <h6
                              v-if="errors[0] && !isProcessing"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              {{ errors[0] }}
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row class="w-100 my-0 mx-auto">
                      <b-col class="mb-4 mt-3" md="12">
                        <ValidationProvider
                          name="Billing Email"
                          rules="email"
                          ref="emailValidator"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            class="position-relative"
                            label="Billing Email"
                          >
                            <b-form-input
                              placeholder="Enter email"
                              v-model="updatedCompany.billingEmail"
                              :disabled="isProcessing"
                              type="text"
                              @input="emailError = false"
                              data-cy="company-edit-field-billing-email"
                            ></b-form-input>
                            <h6
                              v-if="errors[0] && !isProcessing"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              {{ errors[0] }}
                            </h6>
                            <h6
                              v-if="emailError"
                              class="
                                font-sm font-family-regular
                                position-absolute
                                danger
                              "
                            >
                              Company with this email already exist
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>

                    <b-row
                      v-if="updateError && !isProcessing"
                      class="archive-error w-100 my-0 pl-4"
                    >
                      <b-col data-cy="company-edit-error-msg">
                        {{ updateError }}
                      </b-col>
                    </b-row>

                    <b-row
                      class="
                        w-100
                        my-0
                        mt-5
                        mx-auto
                        d-flex
                        justify-content-end
                        button-row
                      "
                    >
                      <b-button
                        @click="emitUpdate"
                        class="
                          cy-current-item-archive
                          btn-primary btn-danger
                          px-5
                          py-3
                          btn-pill
                          ml-2
                          reduce-whitespace
                        "
                        data-cy="company-button-add-update"
                      >
                        <b-spinner
                          v-if="isProcessing"
                          style="width: 14px; height: 14px"
                          variant="light"
                          label="Spinning"
                        ></b-spinner>
                        {{ `${isProcessing ? "" : actionLabel}` }}
                      </b-button>
                    </b-row>
                  </div>
                </b-card>
              </b-form-group>
            </b-form>
          </ValidationObserver>
        </b-col>
      </b-row>
    </b-container>

    <CropperModal
      v-if="imageURL"
      :showCropper="!!selectedFile"
      :fileURL="imageURL"
      @update-image-blob="updateCompanyAvatar"
      @close-and-clear-modal="closeAndClearCropperModal"
    />
  </b-modal>
</template>

<script>
import {
  ValidationObserver,
  ValidationProvider
} from "vee-validate/dist/vee-validate.full";

import { getInitialsFromName } from "@/utils/utils";
import {
  countriesListJSON,
  ACCEPTED_IMAGE_FILE_TYPES
} from "@/utils/constants";

import AvatarItem from "./AvatarItem.vue";
import CropperModal from "./CropperModal.vue";

export default {
  components: {
    AvatarItem,
    CropperModal,
    ValidationObserver,
    ValidationProvider
  },

  props: {
    isOpen: { type: Boolean },
    isProcessing: { type: Boolean },
    updateError: { type: String },

    id: { type: String },
    name: { type: String },
    logo: { type: String },
    headroomEntity: { type: String },
    country: { type: String },
    billingEmail: { type: String },

    memberships: { type: Array }
  },

  created() {
    this.setCountryOptions();
    this.initialiseValues();
  },

  data() {
    return {
      ACCEPTED_IMAGE_FILE_TYPES,

      updatedCompany: { country: "" },

      selectedFile: null,
      imageURL: null,
      companyImageFile: null,
      companyImageBlob: null,

      photoError: "",
      photoFileTypeError: "",
      photoTokenError: "",

      emailError: "",

      countries: [],

      headroomEntityOptions: [
        { value: null, text: "Please select a Billing Entity" },
        { value: "EU", text: "Public Support Assistance BV (EU)" },
        { value: "US", text: "Public Support Assistance Services LLC (US)" }
      ],

      allowsFlexibleHours: false
    };
  },

  computed: {
    isNewCompany() {
      return !this.id;
    },

    actionLabel() {
      return this.isNewCompany ? "Create" : "Update";
    }
  },

  methods: {
    setCountryOptions() {
      const defaultCountryOption = {
        value: null,
        text: "Please select a country."
      };

      this.countries = countriesListJSON.map(({ name }) => ({
        value: name.charAt(1).toUpperCase() + name.slice(1).toLowerCase(),
        text: name
      }));

      this.countries = [defaultCountryOption, ...this.countries];
    },

    initialiseValues() {
      this.updatedCompany.name = this.name;
      this.updatedCompany.headroomEntity = this.headroomEntity || null;
      this.updatedCompany.new_membership = null;
      this.updatedCompany.country = this.country || null;
      this.updatedCompany.billingEmail = this.billingEmail || null;
      this.companyImageFile = this.logo;
    },

    getInitialsFromName,

    close() {
      this.$emit("close-company-modal");
    },

    async emitUpdate() {
      const valid = await this.$refs.observer.validate();
      const isInvalid = !valid;
      if (isInvalid) {
        // * We stop early and the validation tools will show the errors.
        return;
      }

      const updates = {
        data: this.updatedCompany,
        blob: this.companyImageBlob,
        fileName: this.selectedFile?.name
      };

      this.$emit("confirm-update", updates);
    },

    // todo: Move to Avatar upload component
    uploadClear(event) {
      event.target.value = "";
      this.photoFileTypeError = false;
    },

    // todo: Move to Avatar upload component
    uploadFile(event) {
      const extensions = this.ACCEPTED_IMAGE_FILE_TYPES.split(",").filter(
        item => item[0] === "."
      );

      this.selectedFile = event.target.files[0];
      const extension = this.selectedFile.name.split(".").reverse()[0];
      const fileExtension = `.${extension}`;

      const isRestrictedFileType = !extensions.includes(fileExtension);
      if (isRestrictedFileType) {
        this.photoFileTypeError = true;
        event.target.value = "";
        return;
      }

      this.imageURL = URL.createObjectURL(this.selectedFile);
    },

    // todo: Move to Avatar upload component
    updateCompanyAvatar(blob) {
      // todo: return file when moved to component

      this.companyImageBlob = blob;

      const file = new File([blob], blob.name);
      this.companyImageFile = URL.createObjectURL(file);
    },

    closeAndClearCropperModal() {
      this.imageURL = null;
    },

    updateHeadroomEntity(headroomEntity) {
      this.updatedCompany.headroomEntity = headroomEntity;
    },

    updateMembership(membership) {
      this.updatedCompany.new_membership = membership;

      const isEmpty = !this.memberships.length;
      if (isEmpty) {
        this.allowsFlexibleHours = false;
        return;
      }

      const newMembershipLabel = this.memberships.find(
        ({ value }) => value === membership
      )?.text;

      // * @suraj: this is local & weak as it is relying on a hardcoded string
      this.allowsFlexibleHours = !!newMembershipLabel?.includes("flexible");
    },

    updateCountry(country) {
      this.updatedCompany.country = country;
    },

    resetUpdatedCompany() {
      this.updatedCompany = {};
      this.allowsFlexibleHours = false;
    }
  },

  watch: {
    isOpen(value) {
      if (value) {
        this.initialiseValues();
        return;
      }

      this.resetUpdatedCompany();
    }
  }
};
</script>

<style scoped>
button.btn.close-btn {
  right: -2.5rem;
  top: -2rem;
  background: none;
}

.button-row {
  padding-left: 15px;
  padding-right: 15px;
}

.general-info-card {
  border: none;
}
</style>